import CryptoJS, { AES } from "crypto-js";

// ===== Helpers =====
// const chunkMB = 16;
// const chunkSize = chunkMB * 1024 * 1024;
const enc = new TextEncoder();
const dec = new TextDecoder();

/**
 * @param {number} n
 */
function u64be(n) {
  const v = new DataView(new ArrayBuffer(8));
  const hi = Math.floor(n / 2 ** 32) >>> 0;
  const lo = n >>> 0;
  v.setUint32(0, hi); // big-endian 상위 32비트
  v.setUint32(4, lo); // big-endian 하위 32비트

  return new Uint8Array(v.buffer);
}
/**
 * @param {Uint8Array} u8
 */
function b64(u8) {
  return btoa(String.fromCharCode(...u8));
}
/**
 * @param {string} s
 */
function b64d(s) {
  const bin = atob(s);
  const u8 = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
  return u8;
}
/**
 * @param {ArrayBuffer | Uint8Array} buf
 */
async function sha256Hex(buf) {
  const a = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  const d = await crypto.subtle.digest("SHA-256", a);
  return [...new Uint8Array(d)]
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}
/**
 * @param {Uint8Array} keyRaw
 * @param {Uint8Array} msg
 */
async function hmacSha256(keyRaw, msg) {
  const key = await crypto.subtle.importKey(
    "raw",
    keyRaw,
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"],
  );
  const sig = await crypto.subtle.sign("HMAC", key, msg);
  return new Uint8Array(sig);
}
/**
 * @param {Uint8Array} dekRaw
 * @param {number} chunkIndex
 */
async function ivFrom(dekRaw, chunkIndex) {
  const mac = await hmacSha256(dekRaw, u64be(chunkIndex));
  return mac.slice(0, 12);
}
async function genDEK() {
  return crypto.subtle.generateKey({ name: "AES-GCM", length: 256 }, true, [
    "encrypt",
    "decrypt",
  ]);
}
/**
 * @param {CryptoKey} key
 */
async function exportRaw(key) {
  const raw = await crypto.subtle.exportKey("raw", key);
  return new Uint8Array(raw);
}

/**
 * @param {string} password
 * @param {Uint8Array} salt
 */
async function deriveKEK(password, salt) {
  const material = await crypto.subtle.importKey(
    "raw",
    enc.encode(password),
    "PBKDF2",
    false,
    ["deriveKey"],
  );
  return crypto.subtle.deriveKey(
    { name: "PBKDF2", salt, iterations: 200_000, hash: "SHA-256" },
    material,
    { name: "AES-GCM", length: 256 },
    true,
    ["wrapKey", "unwrapKey"],
  );
}
/**
 * @param {CryptoKey} dek
 * @param {CryptoKey} kek
 */
async function wrapDEK(dek, kek) {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const wrapped = await crypto.subtle.wrapKey("raw", dek, kek, {
    name: "AES-GCM",
    iv,
  });
  return { wrapped: new Uint8Array(wrapped), iv };
}
/**
 * @param {Uint8Array} wrapped
 * @param {CryptoKey} kek
 * @param {Uint8Array} iv
 */
async function unwrapDEK(wrapped, kek, iv) {
  const dek = await crypto.subtle.unwrapKey(
    "raw",
    wrapped,
    kek,
    { name: "AES-GCM", iv },
    { name: "AES-GCM", length: 256 },
    true,
    ["encrypt", "decrypt"],
  );
  return dek;
}
/**
 * @param {CryptoKey} dek
 * @param {string} filedId
 * @param {number} chunkIndex
 * @param {ArrayBuffer} plain
 * @param {Uint8Array} iv
 */
async function encryptChunk(dek, fileId, chunkIndex, plain, iv) {
  const aad = enc.encode(`${fileId}:${chunkIndex}`);
  const cipher = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv, additionalData: aad },
    dek,
    plain,
  );
  return new Uint8Array(cipher);
}
/**
 * @param {CryptoKey} dek
 * @param {string} filedId
 * @param {number} chunkIndex
 * @param {ArrayBuffer} plain
 * @param {Uint8Array} iv
 */
async function decryptChunk(dek, fileId, chunkIndex, cipher, iv) {
  const aad = enc.encode(`${fileId}:${chunkIndex}`);
  const plain = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv, additionalData: aad },
    dek,
    cipher,
  );
  return new Uint8Array(plain);
}
/**
 * @param {string} text
 */
function part(text) {
  return new TextEncoder().encode(text);
}

/**
 * @param {Uint8Array} hay
 * @param {Uint8Array} needle
 * @param {number?} from
 */
function indexOfSeq(hay, needle, from = 0) {
  // simple scan
  for (let i = from; i <= hay.length - needle.length; i++) {
    let ok = true;
    for (let j = 0; j < needle.length; j++)
      if (hay[i + j] !== needle[j]) {
        ok = false;
        break;
      }
    if (ok) return i;
  }
  return -1;
}

/**
 * @param {ArrayBuffer} buf
 */
async function parseContainer(buf) {
  const u8 = new Uint8Array(buf);
  const m1 = new TextEncoder().encode("ENCXv1\n");
  const mh = new TextEncoder().encode("--HEADER--\n");
  const mc = new TextEncoder().encode("--CHUNKS--\n");
  const mt = new TextEncoder().encode("\n--TOC--\n");
  const me = new TextEncoder().encode("--END--\n");

  const i1 = indexOfSeq(u8, m1, 0);
  if (i1 !== 0) throw new Error("Bad container magic");
  const ih = indexOfSeq(u8, mh, m1.length);
  if (ih < 0) throw new Error("HEADER marker missing");
  const ic = indexOfSeq(u8, mc, ih + mh.length);
  if (ic < 0) throw new Error("CHUNKS marker missing");

  const it = indexOfSeq(u8, mt, ic + mc.length);
  if (it < 0) throw new Error("TOC marker missing");
  const ie = indexOfSeq(u8, me, it + mt.length);
  if (ie < 0) throw new Error("END marker missing");

  const headerStart = ih + mh.length;
  const headerEnd = ic; // header JSON (ends just before CHUNKS marker)
  const headerStr = dec.decode(u8.slice(headerStart, headerEnd)).trim();
  const header = JSON.parse(headerStr);

  const chunksStart = ic + mc.length;
  const chunksEnd = it; // chunks end right before "\n--TOC--\n"

  const tocStart = it + mt.length;
  const tocEnd = ie;
  const tocStr = dec.decode(u8.slice(tocStart, tocEnd)).trim();

  /** @type {Array<{idx:number;offset:number;size:number;sha256:string}>} */
  const toc = JSON.parse(tocStr);

  return { header, toc, chunksStart, chunksEnd, u8 };
}

/**
 * 암호화 파일의 "타입(알고리즘/포맷)"을 식별한다.
 * - 헤더가 없을 수 있으므로, 시그니처/패턴/확장자/후보 시도 순으로 판단
 * - 확실히 식별 불가하면 null 반환
 *
 * @param {File|ArrayBuffer|Uint8Array|string} src
 * @param {function(string):boolean} [opts.tryDecryptProbe] // (algo) => 복호 시도해보고 성공여부 리턴하는 프로브(선택)
 * @returns {Primise<{format: 'ENCXv1'|null, cipher: string|null, reason: string}>}
 */
async function detectEncryptedType(src) {
  // ---- 1) 바이트 접근 준비
  let buf;
  if (src instanceof File)
    buf = new Uint8Array(
      await src.slice(0, Math.min(src.size, 4096)).arrayBuffer(),
    );
  else if (src instanceof ArrayBuffer)
    buf = new Uint8Array(src).subarray(0, Math.min(src.byteLength, 4096));
  else if (src instanceof Uint8Array)
    buf = src.subarray(0, Math.min(src.byteLength, 4096));
  else if (typeof src === "string") {
    const u8 = b64d(src)
    buf = u8.subarray(0, Math.min(u8.byteLength, 4096));
  } else throw new Error("Unsupported src");

  const headStr = new TextDecoder().decode(buf);

  // 헬퍼
  const starts = (s) => headStr.startsWith(s);

  // ---- 2) 커스텀 포맷/알려진 시그니처
  // (A) ENCXv1: 우리가 정의한 컨테이너 프렐류드
  if (starts("ENCXv1\n")) {
    return {
      format: "ENCXv1",
      cipher: "AES-256-GCM",
      reason: "Magic ENCXv1 detected",
    };
  }

  return {
    format: null,
    cipher: "AES-256",
    reason: "No header/magic matched; default detected",
  };
}

/**
 * @param {File} file
 * @param {string} encryptKey
 * @param {number?} chunkSize
 * @param {function} callback
 * @returns
 */
const handleEncrypt = async (file, encryptKey, chunkSize = 16 * 1000 * 1000, callback) => {
  if (!file) return Promise.reject("파일을 선택하세요");
  if (!encryptKey) return Promise.reject("비밀번호를 입력하세요 (DEK 래핑용)");

  try {
    const totalChunks = Math.ceil(file.size / chunkSize);
    // setEncProgress({ done: 0, total: totalChunks });
    if (typeof callback === "function" && callback) {
      callback({ done: 0, total: totalChunks });
    }

    const fileId = crypto.randomUUID();
    const dek = await genDEK();
    const dekRaw = await exportRaw(dek);
    const salt = crypto.getRandomValues(new Uint8Array(16));
    const kek = await deriveKEK(encryptKey, salt);
    const { wrapped, iv: wrapIv } = await wrapDEK(dek, kek);

    /** @type {Array<{idx:number;offset:number;size:number;sha256:string}>} */
    const toc = [];
    /** @type {Uint8Array[]} */
    const chunkParts = [];
    let offset = 0;

    for (let idx = 0; idx < totalChunks; idx++) {
      const start = idx * chunkSize;
      const end = Math.min(file.size, start + chunkSize);
      const plain = await file.slice(start, end).arrayBuffer();

      const iv = await ivFrom(dekRaw, idx);
      const cipher = await encryptChunk(dek, fileId, idx, plain, iv);
      const hash = await sha256Hex(cipher);

      chunkParts.push(cipher);
      toc.push({ idx, offset, size: cipher.byteLength, sha256: hash });
      offset += cipher.byteLength;

      if (typeof callback === "function" && callback) {
        callback({ done: idx + 1, total: totalChunks });
      }
      await new Promise((r) => setTimeout(r));
    }

    const header = {
      schema: 1,
      container: "ENCX",
      cipher: "AES-256-GCM",
      ivDerivation: "HMAC(dekRaw, chunkIndex)[:12]",
      aadFormat: "${fileId}:${chunkIndex}",
      fileId,
      fileName: file.name,
      fileSize: file.size,
      mime: file.type || "application/octet-stream",
      chunkSize,
      totalChunks,
      salt: b64(salt),
      wrappedDEK: b64(wrapped),
      wrapIv: b64(wrapIv),
      tagBytes: 16,
    };

    const headerJson = JSON.stringify(header);
    const tocJson = JSON.stringify(toc);

    const blob = new Blob(
      [
        part("ENCXv1\n"),
        part("--HEADER--\n"),
        part(headerJson + "\n"),
        part("--CHUNKS--\n"),
        ...chunkParts,
        part("\n--TOC--\n"),
        part(tocJson + "\n"),
        part("--END--\n"),
      ],
      { type: "application/octet-stream" },
    );

    // const url = URL.createObjectURL(blob);
    // setOutUrl(url);
    // setManifest({ header, toc });
    return { blob, header, toc };
  } catch (e) {
    console.error(e);
    alert("암호화 중 오류: " + (e?.message || e));
  }
};

/**
 *
 * @param {File} encxFile
 * @param {string} decryptKey
 * @param {function} callback
 * @returns
 */
const handleDecrypt = async (encxFile, decryptKey, callback) => {
  if (!encxFile) throw new Error("암호화 컨테이너(.encx)를 선택하세요");
  if (!decryptKey) throw new Error("비밀번호를 입력하세요 (DEK 언래핑용)");

  try {
    const buf = await encxFile.arrayBuffer();
    const { header, toc, chunksStart, u8 } = await parseContainer(buf);

    // 1) derive KEK & unwrap DEK
    const salt = b64d(header.salt);
    const wrapIv = b64d(header.wrapIv);
    const wrappedDEK = b64d(header.wrappedDEK);

    const kek = await deriveKEK(decryptKey, salt);
    const dek = await unwrapDEK(wrappedDEK, kek, wrapIv);
    const dekRaw = await exportRaw(dek);

    // 2) decrypt chunks
    if (typeof callback === "function" && callback) {
      callback({ done: 0, total: toc.length });
    }
    /** @type {Uint8Array[]} */
    const plains = [];

    for (let i = 0; i < toc.length; i++) {
      const { idx, offset, size, sha256 } = toc[i];
      const start = chunksStart + offset;
      const end = start + size;
      const cipher = u8.slice(start, end);

      const hash = await sha256Hex(cipher);
      if (hash !== sha256) throw new Error(`무결성 오류: chunk ${idx}`);

      const iv = await ivFrom(dekRaw, idx);
      const plain = await decryptChunk(dek, header.fileId, idx, cipher, iv);
      plains.push(plain);

      if (typeof callback === "function" && callback) {
        callback({ done: i + 1, total: toc.length });
      }
      await new Promise((r) => setTimeout(r));
    }

    // 3) rebuild Blob & download link
    const outBlob = new Blob(plains, {
      type: header.mime || "application/octet-stream",
    });
    const url = URL.createObjectURL(outBlob);
    // setPlainUrl(url);
    // setPlainMeta({
    //   name: header.fileName || "decrypted.bin",
    //   mime: header.mime || "application/octet-stream",
    //   size: outBlob.size,
    // });
    return { url };
  } catch (e) {
    console.error(e);
    throw new Error("복호화 중 오류: " + (e?.message || e));
  }
};

/**
 *
 * @param {File} file
 * @param {string} encryptKey
 * @returns
 */
const oldHandleEncrypt = (file, encryptKey) =>
  new Promise((resolve, reject) => {
    const fileReader = new FileReader();
    // console.log(`[KEY]`, `${encryptKey}`);

    fileReader.onload = ({ target }) => {
      const base64 = target.result;
      // console.log("#########[FILE]", target.result);
      // 3. 암호화
      const encode = AES.encrypt(base64, encryptKey).toString();
      // console.log("[FILE ENCODE]", encode);

      // const _files = new FormData();
      // _files.append('file', encode, encode.name)
      // _files.append('file', file, file.name)
      // console.log("[FILE BB]", _files);
      const _blob = new Blob([encode]);
      resolve(_blob);
      // _files.append("file", _blob, file.name);
    };
    fileReader.onerror = (error) => {
      reject(error);
    };

    fileReader.readAsDataURL(file);
  });

/**
 *
 * @param {string} payload base64형태의 파일
 * @param {string} decryptKey
 * @returns
 */
const oldHandleDecrypt = (payload, decryptKey) =>
  new Promise((resolve, reject) => {
    try {
      const decodeBase64 = atob(payload);
      const decode = AES.decrypt(decodeBase64, decryptKey).toString(
        CryptoJS.enc.Utf8,
      );
      resolve(decode);
    } catch (error) {
      reject(error);
    }
  });

/**
 *
 * @param {string} payload
 * @param {string} name
 */
const downloadFile = (payload, name) => {
  const src = payload;
  const link = document.createElement("a");
  link.href = src;
  link.download = name;
  link.click();
};

export {
  handleEncrypt as encryptFile,
  handleDecrypt as decryptFile,
  oldHandleEncrypt as oldEncryptFile,
  oldHandleDecrypt as oldDecryptFile,
  downloadFile,
  b64d,
  detectEncryptedType,
};
